/**
 * Document Attachment Step Component
 *
 * New step in the title/escrow assignment flow for attaching documents.
 * Allows users to:
 * 1. Select documents from Encompass eFolder (with live preview)
 * 2. Upload documents from their desktop
 *
 * Documents are attached globally to the portal (not to specific conditions).
 *
 * Layout: 30/70 split with picker on left, preview on right
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Upload,
  X,
  FileText,
  FolderOpen,
  ArrowLeft,
  ArrowRight,
  Eye,
  AlertCircle,
  CheckCircle2,
  Trash2,
  HardDrive,
  Monitor
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { DocumentPickerFromEfolder, getSelectedAttachments } from './DocumentPickerFromEfolder';
import { EfolderPreviewPane } from '@/components/encompass/prospects/EfolderPreviewPane';
import { getAttachmentUrl } from '@/services/encompassDocumentService';
import type { SelectedDocumentForAttachment } from '@/types/conditions-portal';
import type { EncompassAttachment, EncompassDocumentWithAttachments } from '@/types/encompass-documents';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DocumentAttachmentStep', 'conditions-portal');

// File limits
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

interface DocumentAttachmentStepProps {
  loanId: string;
  selectedDocuments: SelectedDocumentForAttachment[];
  onDocumentsChange: (docs: SelectedDocumentForAttachment[]) => void;
  onBack: () => void;
  onNext: () => void;
  // Pre-fetched document data (loaded when modal opens for faster display)
  encompassDocuments: EncompassDocumentWithAttachments[];
  documentsLoading: boolean;
  documentsError: string | null;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) {
return '';
}
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(0)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Generate a unique ID for upload tracking
 */
function generateUploadId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function DocumentAttachmentStep({
  loanId,
  selectedDocuments,
  onDocumentsChange,
  onBack,
  onNext,
  encompassDocuments,
  documentsLoading,
  documentsError
}: DocumentAttachmentStepProps) {
  // Track selected Encompass attachment IDs
  const [selectedEncompassIds, setSelectedEncompassIds] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    selectedDocuments
      .filter(d => d.source === 'encompass' && d.attachmentId)
      .forEach(d => ids.add(d.attachmentId!));
    return ids;
  });

  // Track uploaded files
  const [uploadedFiles, setUploadedFiles] = useState<SelectedDocumentForAttachment[]>(() =>
    selectedDocuments.filter(d => d.source === 'upload')
  );

  // Track validation errors
  const [fileErrors, setFileErrors] = useState<string[]>([]);

  // Preview state
  const [previewAttachment, setPreviewAttachment] = useState<EncompassAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Cache for preloaded signed URLs (attachmentId -> signedUrl)
  const urlCacheRef = useRef<Map<string, string>>(new Map());

  // Preload signed URLs for all downloaded attachments
  useEffect(() => {
    if (documentsLoading || !encompassDocuments.length) {
return;
}

    const preloadUrls = async () => {
      const downloadedAttachments: EncompassAttachment[] = [];
      for (const doc of encompassDocuments) {
        for (const att of doc.attachments) {
          if (att.download_status === 'downloaded' && att.storage_path && att.is_active !== false) {
            downloadedAttachments.push(att);
          }
        }
      }

      log.info('Preloading signed URLs', { count: downloadedAttachments.length });

      // Fetch URLs in parallel (limit concurrency to avoid overwhelming the server)
      const batchSize = 10;
      for (let i = 0; i < downloadedAttachments.length; i += batchSize) {
        const batch = downloadedAttachments.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (att) => {
            if (urlCacheRef.current.has(att.id)) {
return;
} // Already cached
            try {
              const url = await getAttachmentUrl(att.storage_path!);
              if (url) {
                urlCacheRef.current.set(att.id, url);
              }
            } catch {
              // Silently fail - URL will be fetched on demand if needed
            }
          })
        );
      }

      log.info('Signed URLs preloaded', { cached: urlCacheRef.current.size });
    };

    preloadUrls();
  }, [encompassDocuments, documentsLoading]);

  // Get actual attachment objects for selected Encompass docs (from pre-fetched data)
  const selectedEncompassAttachments = useMemo(
    () => getSelectedAttachments(encompassDocuments, selectedEncompassIds),
    [encompassDocuments, selectedEncompassIds]
  );

  // Combine into SelectedDocumentForAttachment format
  const allSelectedDocuments = useMemo(() => {
    const encompassDocs: SelectedDocumentForAttachment[] = selectedEncompassAttachments.map(att => ({
      id: att.id,
      source: 'encompass' as const,
      attachmentId: att.id,
      displayName: att.title || 'Untitled',
      fileSize: att.file_size || undefined,
      mimeType: att.file_type || undefined
    }));

    return [...encompassDocs, ...uploadedFiles];
  }, [selectedEncompassAttachments, uploadedFiles]);

  // Update parent when selection changes
  const updateParent = useCallback(() => {
    onDocumentsChange(allSelectedDocuments);
  }, [allSelectedDocuments, onDocumentsChange]);

  // Handle Encompass selection change
  const handleEncompassSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedEncompassIds(ids);
    log.info('Encompass selection changed', { count: ids.size });
  }, []);

  // Handle preview attachment selection
  const handlePreviewAttachment = useCallback(async (attachment: EncompassAttachment | null) => {
    setPreviewAttachment(attachment);

    if (!attachment || attachment.download_status !== 'downloaded' || !attachment.storage_path) {
      setPreviewUrl(null);
      return;
    }

    // Check cache first - instant preview if URL is preloaded
    const cachedUrl = urlCacheRef.current.get(attachment.id);
    if (cachedUrl) {
      setPreviewUrl(cachedUrl);
      return;
    }

    // Fallback: fetch URL on demand if not cached
    setPreviewLoading(true);
    try {
      const url = await getAttachmentUrl(attachment.storage_path);
      if (url) {
        urlCacheRef.current.set(attachment.id, url); // Cache for next time
      }
      setPreviewUrl(url);
    } catch (error) {
      log.error('Failed to load preview', { attachmentId: attachment.id, error });
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Validate file
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: `${file.name}: Only PDF, JPG, and PNG files are allowed` };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `${file.name}: File size must be under 25MB` };
    }
    // Check for duplicates
    const isDuplicate = uploadedFiles.some(u => u.displayName === file.name && u.fileSize === file.size);
    if (isDuplicate) {
      return { valid: false, error: `${file.name}: File already added` };
    }
    return { valid: true };
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const errors: string[] = [];
    const validFiles: SelectedDocumentForAttachment[] = [];

    acceptedFiles.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push({
          id: generateUploadId(),
          source: 'upload',
          file,
          displayName: file.name,
          fileSize: file.size,
          mimeType: file.type
        });
      } else if (validation.error) {
        errors.push(validation.error);
      }
    });

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      log.info('Files added for upload', { count: validFiles.length });
    }

    if (errors.length > 0) {
      setFileErrors(errors);
      // Clear errors after 5 seconds
      setTimeout(() => setFileErrors([]), 5000);
    }
  }, [uploadedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  // Remove uploaded file
  const removeUploadedFile = useCallback((id: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
    log.info('Uploaded file removed', { id });
  }, []);

  // Remove Encompass selection
  const removeEncompassDoc = useCallback((attachmentId: string) => {
    setSelectedEncompassIds(prev => {
      const next = new Set(prev);
      next.delete(attachmentId);
      return next;
    });
    log.info('Encompass document removed', { attachmentId });
  }, []);

  // Handle next - update parent and proceed
  const handleNext = () => {
    onDocumentsChange(allSelectedDocuments);
    onNext();
  };

  // Calculate total size
  const totalSize = allSelectedDocuments.reduce((sum, d) => sum + (d.fileSize || 0), 0);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* 30/70 Split Layout */}
      <div className="flex-1 grid grid-cols-[30%_70%] min-h-0">
        {/* Left Panel - Document Picker */}
        <div className="flex flex-col min-h-0 border-r">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {/* Header info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-blue-800">Attach Reference Documents</p>
                    <p className="text-xs text-blue-700 mt-0.5">
                      Select from eFolder or upload. Click to preview.
                    </p>
                  </div>
                </div>
              </div>

              {/* Accordion for the two sources */}
              <Accordion type="multiple" defaultValue={['encompass']} className="space-y-3">
                {/* Encompass eFolder Section */}
                <AccordionItem value="encompass" className="border rounded-lg">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-4 w-4 text-[#183C2E]" />
                      <span className="font-medium text-sm">Encompass eFolder</span>
                      {selectedEncompassIds.size > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 text-xs">
                          {selectedEncompassIds.size}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <DocumentPickerFromEfolder
                      documents={encompassDocuments}
                      loading={documentsLoading}
                      error={documentsError}
                      selectedIds={selectedEncompassIds}
                      onSelectionChange={handleEncompassSelectionChange}
                      onPreviewAttachment={handlePreviewAttachment}
                      previewingId={previewAttachment?.id}
                      maxHeight="280px"
                    />
                  </AccordionContent>
                </AccordionItem>

                {/* Desktop Upload Section */}
                <AccordionItem value="upload" className="border rounded-lg">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-[#183C2E]" />
                      <span className="font-medium text-sm">Upload from Computer</span>
                      {uploadedFiles.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-5 text-xs">
                          {uploadedFiles.length}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    {/* Dropzone */}
                    <div
                      {...getRootProps()}
                      className={cn(
                        'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
                        isDragActive
                          ? 'border-[#183C2E] bg-[#183C2E]/5'
                          : 'border-muted-foreground/25 hover:border-[#183C2E]/50 hover:bg-muted/50'
                      )}
                    >
                      <input {...getInputProps()} />
                      <Upload className={cn(
                        'h-6 w-6 mx-auto mb-2',
                        isDragActive ? 'text-[#183C2E]' : 'text-muted-foreground'
                      )} />
                      {isDragActive ? (
                        <p className="text-[#183C2E] font-medium text-sm">Drop files here...</p>
                      ) : (
                        <>
                          <p className="text-xs font-medium">
                            Drag & drop or click to browse
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            PDF, JPG, PNG up to 25MB
                          </p>
                        </>
                      )}
                    </div>

                    {/* Validation errors */}
                    {fileErrors.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {fileErrors.map((error, i) => (
                          <div key={i} className="flex items-center gap-1 text-xs text-red-600">
                            <AlertCircle className="h-3 w-3 flex-shrink-0" />
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Uploaded files list */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {uploadedFiles.map(file => (
                          <div
                            key={file.id}
                            className="flex items-center gap-2 p-2 bg-muted/50 rounded"
                          >
                            <FileText className="h-4 w-4 text-[#183C2E] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{file.displayName}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.fileSize)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-red-600"
                              onClick={() => removeUploadedFile(file.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Selected Documents Summary */}
              {allSelectedDocuments.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800 text-sm">
                      {allSelectedDocuments.length} Selected
                    </span>
                    <span className="text-xs text-green-600 ml-auto">
                      {formatFileSize(totalSize)}
                    </span>
                  </div>

                  <div className="space-y-1 max-h-[120px] overflow-y-auto">
                    {/* Encompass docs */}
                    {selectedEncompassAttachments.map(att => (
                      <div
                        key={att.id}
                        className="flex items-center gap-1.5 p-1.5 bg-white/50 rounded border border-green-200 text-xs"
                      >
                        <FolderOpen className="h-3 w-3 text-amber-500 flex-shrink-0" />
                        <span className="truncate flex-1">{att.title || 'Untitled'}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-red-600"
                          onClick={() => removeEncompassDoc(att.id)}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                    ))}

                    {/* Uploaded files */}
                    {uploadedFiles.map(file => (
                      <div
                        key={file.id}
                        className="flex items-center gap-1.5 p-1.5 bg-white/50 rounded border border-green-200 text-xs"
                      >
                        <Upload className="h-3 w-3 text-blue-500 flex-shrink-0" />
                        <span className="truncate flex-1">{file.displayName}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-red-600"
                          onClick={() => removeUploadedFile(file.id)}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex flex-col min-h-0 bg-muted/10">
          <EfolderPreviewPane
            selectedAttachment={previewAttachment}
            previewUrl={previewUrl}
            previewLoading={previewLoading}
            onRetrySync={() => {}}
            syncing={false}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t bg-background flex gap-2">
        <Button variant="outline" onClick={onBack} className="flex-1">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-gradient-to-r from-[#183C2E] to-[#1F4D3A] hover:from-[#1F4D3A] hover:to-[#183C2E]"
        >
          {allSelectedDocuments.length > 0 ? (
            <>
              <Eye className="h-4 w-4 mr-2" />
              Continue with {allSelectedDocuments.length} doc{allSelectedDocuments.length !== 1 ? 's' : ''}
            </>
          ) : (
            <>
              Skip
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default DocumentAttachmentStep;
